import { Http } from '@mygpt/app/http';

/**
 * Stripe Checkout Session请求参数
 */
export interface CheckoutSessionRequest {
  /** Stripe Price ID（套餐价格ID） */
  price_id: string;
  /** 支付成功后的回调URL */
  success_url: string;
  /** 支付取消后的回调URL */
  cancel_url: string;
  /** v0.8: 咨询服务加购列表（每项 {service_id, quantity}） */
  consulting_items?: Array<{ service_id: string; quantity: number }>;
}

/**
 * Stripe Checkout Session响应
 */
export interface CheckoutSessionResponse {
  /** Stripe Checkout页面URL */
  checkout_url: string;
  /** Session ID */
  session_id: string;
}

/**
 * 订阅状态枚举
 */
export enum SubscriptionStatus {
  /** 活跃中 */
  ACTIVE = 'active',
  /** 已取消 */
  CANCELED = 'canceled',
  /** 未完成 */
  INCOMPLETE = 'incomplete',
  /** 未完成-已过期 */
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  /** 逾期未支付 */
  PAST_DUE = 'past_due',
  /** 试用中 */
  TRIALING = 'trialing',
  /** 未付款 */
  UNPAID = 'unpaid',
}

/**
 * 订阅信息
 */
export interface SubscriptionPlanBrief {
  id: string;
  name: string;
  price: number | null;
}

export interface SubscriptionPriceBrief {
  id: string;
  unit_amount: number;
  currency: string;
  billing_interval: 'month' | 'year';
}

export interface ConsultingItemBrief {
  name: string;
  quantity: number;
  unit_amount: number;
  currency: string;
}

export interface SubscriptionResponse {
  /** 订阅ID */
  id: string;
  /** Stripe 订阅ID */
  stripe_subscription_id?: string;
  /** 订阅状态 */
  status: SubscriptionStatus;
  /** 套餐信息 */
  plan: SubscriptionPlanBrief;
  /** 价格信息 */
  price: SubscriptionPriceBrief;
  /** 当前周期开始时间 */
  current_period_start: string;
  /** 当前周期结束时间 */
  current_period_end: string;
  /** 是否在周期结束时取消 */
  cancel_at_period_end: boolean;
  /** 取消时间 */
  canceled_at?: string;
  /** 创建时间 */
  created_at?: string;
  /** 更新时间 */
  updated_at?: string;
  /** v0.8 订阅含的咨询服务行 */
  consulting_items?: ConsultingItemBrief[];
  /** 兼容旧字段 */
  user_id?: string;
  plan_id?: string;
  plan_name?: string;
}

/**
 * 预订阅信息
 */
export interface ScheduledSubscription {
  id: string;
  stripe_schedule_id: string;
  status: string;
  plan: SubscriptionPlanBrief;
  price: SubscriptionPriceBrief;
  /** 预计开始时间 */
  scheduled_start_date: string;
}

/**
 * 配额信息
 */
export interface QuotaInfo {
  /** 是否为企业用户 */
  is_corporate: boolean;
  /** Bot配额 */
  bot_quota: {
    used: number;
    limit: number;
    available: number;
  };
  /** 消息配额 */
  message_quota: {
    used: number;
    limit: number;
    available: number;
  };
  /** 网页配额 */
  web_page_quota: {
    used: number;
    limit: number;
    available: number;
  };
  /** 多模态解析配额 */
  multi_modal_parsing_quota: {
    used: number;
    limit: number;
    available: number;
  };
}

/**
 * 订阅详情完整响应
 */
export interface SubscriptionDetailResponse {
  /** 订阅信息 */
  subscription: SubscriptionResponse;
  /** 预订阅信息 */
  scheduled_subscription?: ScheduledSubscription;
  /** 配额信息 */
  quota: QuotaInfo;
}

/**
 * 取消订阅请求
 */
export interface CancelSubscriptionRequest {
  /** 是否在周期结束时取消（true: 周期结束时取消, false: 立即取消） */
  cancel_at_period_end?: boolean;
  /** 取消 pending schedule */
  schedule_id?: string;
  /** 取消指定订阅 */
  subscription_id?: string;
}

/**
 * 取消订阅响应
 */
export interface CancelSubscriptionResponse {
  /** 取消类型: scheduled_subscription 或 subscription */
  cancelled_type: 'scheduled_subscription' | 'subscription';
}

/**
 * 变更订阅请求
 */
export interface ChangeSubscriptionRequest {
  /** 新的套餐ID */
  new_plan_id: string;
  /** 是否立即变更（false表示在下个周期变更） */
  change_immediately?: boolean;
}

/**
 * 套餐信息
 */
export interface PlanInfo {
  /** 套餐ID */
  id: string;
  /** 套餐名称 */
  name: string;
  /** 套餐类型 */
  plan_type: string;
  /** 套餐等级标识，用于匹配官网卡片（如 スターター、プロフェッショナル） */
  plan_level: string | null;
  /** 套餐价格（日元） */
  price: number | null;
  /** 套餐描述 */
  description: string;
  /** 消息配额，null表示无限制 */
  message_quota: number | null;
  /** 存储配额（字节），null表示无限制 */
  storage_quota: number | null;
  /** Bot配额，null表示无限制 */
  bot_quota: number | null;
  /** マルチモーダル分析配额，null表示无限制 */
  multi_modal_parsing_quota: number | null;
}

/**
 * 价格信息
 */
export interface PriceInfo {
  /** Stripe Price ID */
  price_id: string;
  /** 套餐信息 */
  plan: PlanInfo;
  /** 单价（单位：分/cent） */
  unit_amount: number;
  /** 货币代码（如：jpy, usd） */
  currency: string;
  /** 计费周期 */
  billing_interval: 'month' | 'year';
  /** 计费周期数量 */
  billing_interval_count: number;
}

/**
 * 价格列表响应
 */
export interface PricesResponse {
  prices: PriceInfo[];
}

/**
 * 创建Stripe Checkout Session
 */
export const createCheckoutSession = (params: CheckoutSessionRequest) => {
  const { post } = Http.use();
  return post<CheckoutSessionResponse>('/billing/checkout/session', params);
};

/**
 * 获取当前用户的订阅详情
 */
export const getCurrentSubscription = () => {
  const { get } = Http.use();
  return get<SubscriptionDetailResponse>('/billing/subscription');
};

/**
 * 取消当前订阅
 */
export const cancelSubscription = (params: CancelSubscriptionRequest = {}) => {
  const { post } = Http.use();
  return post<CancelSubscriptionResponse>('/billing/subscription/cancel', params);
};

/**
 * 重新激活已预约取消的订阅
 */
export const reactivateSubscription = () => {
  const { post } = Http.use();
  return post('/billing/subscription/reactivate');
};

/**
 * 变更订阅套餐（升级/降级）
 */
export const changeSubscription = (params: ChangeSubscriptionRequest) => {
  const { post } = Http.use();
  return post('/billing/subscription/change', params);
};

/**
 * 获取所有可用的套餐价格
 */
export const getAvailablePrices = () => {
  const { get } = Http.use();
  return get<PricesResponse>('/billing/prices');
};

/**
 * 账单状态枚举
 */
export enum BillingStatus {
  /** 支付成功 */
  SUCCEEDED = 'succeeded',
  /** 已退款 */
  REFUNDED = 'refunded',
  /** 部分退款 */
  PARTIALLY_REFUNDED = 'partially_refunded',
  /** 待支付 */
  PENDING = 'pending',
  /** 支付失败 */
  FAILED = 'failed',
}

/**
 * 账单记录项
 */
export interface BillingHistoryItem {
  /** 账单ID */
  id: string;
  /** 账单日期 */
  billing_date: string;
  /** 套餐名称 */
  plan_name: string;
  /** 金额（单位：分） */
  amount: number;
  /** 格式化后的金额显示 */
  amount_display: string;
  /** 货币代码 */
  currency: string;
  /** 账单状态 */
  status: BillingStatus;
  /** 发票号 */
  invoice_number: string;
  /** 发票在线查看URL */
  invoice_url: string;
  /** 发票PDF下载URL */
  invoice_pdf_url: string;
}

/**
 * 账单历史分页响应
 */
export interface BillingHistoryResponse {
  /** 账单记录列表 */
  items: BillingHistoryItem[];
  /** 总记录数 */
  total: number;
  /** 当前页码 */
  page: number;
  /** 每页大小 */
  page_size: number;
  /** 总页数 */
  total_pages: number;
}

/**
 * 账单历史查询参数
 */
export interface BillingHistoryParams {
  /** 页码（从1开始） */
  page?: number;
  /** 每页大小 */
  page_size?: number;
}

/**
 * 获取账单历史
 */
export const getBillingHistory = (params: BillingHistoryParams = {}) => {
  const { get } = Http.use();
  const { page = 1, page_size = 20 } = params;
  return get<BillingHistoryResponse>(`/billing/history?page=${page}&page_size=${page_size}`);
};

/**
 * 账单统计信息
 */
export interface BillingStatistics {
  /** 总支出金额（单位：分） */
  total_spent: number;
  /** 格式化后的总支出显示 */
  total_spent_display: string;
  /** 总账单数 */
  total_invoices: number;
  /** 月均支出（单位：分） */
  monthly_average: number;
  /** 格式化后的月均支出显示 */
  monthly_average_display: string;
  /** 货币代码 */
  currency: string;
}

/**
 * 获取账单统计信息
 */
export const getBillingStatistics = () => {
  const { get } = Http.use();
  return get<BillingStatistics>('/billing/statistics');
};

/**
 * 导出账单历史参数
 */
export interface ExportBillingParams {
  /** 语言代码（默认：ja） */
  lang?: 'ja' | 'zh' | 'en';
}

/**
 * 导出账单历史为Excel文件
 */
export const exportBillingHistory = async (params: ExportBillingParams = {}) => {
  const { lang = 'ja' } = params;
  const http = Http.use();

  const response = await http.get(`/billing/export?lang=${lang}`, {
    responseType: 'blob',
  });

  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `billing_history_${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();

  setTimeout(() => {
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, 100);

  return { success: true };
};

// ============================================================
// Dealer 渠道扩展（v0.5）—— 独立 endpoint，不影响既有调用
// 详见 docs/kaga-coupon/02-技術方案_加賀電子クーポン発行機能.md §4.5 §5.4
// ============================================================

export type CheckoutPromoStatus =
  | 'none'
  | 'valid'
  | 'not_found'
  | 'expired'
  | 'used_up'
  | 'wrong_customer'
  | 'inactive';

export interface CheckoutPreviewRequest {
  price_id: string;
  promo?: string;
  /** v0.8 多咨询服务加购 */
  consulting_items?: Array<{ service_id: string; quantity: number }>;
}

export interface CheckoutPreviewConsultingLine {
  service_id: string;
  name: string;
  unit_amount: number;
  quantity: number;
  subtotal: number;
}

export interface CheckoutPreviewDiscount {
  amount: number;
  coupon_name: string;
  dealer_name: string;
}

export interface CheckoutPreviewTrial {
  trial_end_at: string;
  description_key: string;
}

export interface CheckoutPreviewFirstCharge {
  amount: number;
  charged_at: string;
}

export interface CheckoutPreviewRecurring {
  amount: number;
  interval: 'month' | 'year';
  next_renewal_at: string;
}

export interface CheckoutPreviewResponse {
  plan_name: string;
  standard_amount: number;
  currency: string;
  promo_status: CheckoutPromoStatus;
  promo_message?: string;
  discount?: CheckoutPreviewDiscount;
  trial?: CheckoutPreviewTrial;
  first_charge: CheckoutPreviewFirstCharge;
  recurring: CheckoutPreviewRecurring;
  /** v0.8 多咨询服务加购预览（list 为空时不渲染） */
  consulting_items?: CheckoutPreviewConsultingLine[];
}

export const previewCheckout = (params: CheckoutPreviewRequest) => {
  const { post } = Http.use();
  return post<CheckoutPreviewResponse>('/billing/checkout/preview', params);
};

export interface CheckoutWithPromoRequest extends CheckoutSessionRequest {
  promo: string;
}

export const createCheckoutSessionWithPromo = (params: CheckoutWithPromoRequest) => {
  const { post } = Http.use();
  return post<CheckoutSessionResponse>('/billing/checkout/session-with-promo', params);
};

// ============================================================
// 咨询服务加购（v0.8）
// ============================================================

export interface ConsultingServiceInfo {
  id: string;
  name: string;
  description?: string | null;
  unit_amount: number;
  currency: string;
  max_quantity_per_subscription: number;
}

export const listConsultingServices = () => {
  const { get } = Http.use();
  return get<ConsultingServiceInfo[]>('/billing/consulting/services');
};

export interface ConsultingItemReq {
  service_id: string;
  quantity: number;
}

export interface ConsultingItemsUpdateRequest {
  items: ConsultingItemReq[];
}

export interface ConsultingItemsUpdateResponse {
  subscription_id: string;
  applied: ConsultingItemReq[];
  effective: 'next_cycle';
}

export const updateConsultingItems = (req: ConsultingItemsUpdateRequest) => {
  const { patch } = Http.use();
  return patch<ConsultingItemsUpdateResponse>('/billing/subscription/consulting-items', req);
};

// ============================================================
// 咨询服务后台管理（超管，v0.8）
// ============================================================

export interface AdminConsultingService {
  id: string;
  name: string;
  description?: string | null;
  stripe_product_id: string;
  stripe_price_id: string;
  unit_amount: number;
  currency: string;
  max_quantity_per_subscription: number;
  is_active: boolean;
}

export interface AdminConsultingServiceCreate {
  name: string;
  description?: string | null;
  unit_amount: number;
  currency?: string;
  max_quantity_per_subscription?: number;
}

export interface AdminConsultingServiceUpdate {
  name?: string;
  description?: string | null;
  max_quantity_per_subscription?: number;
  is_active?: boolean;
}

export const listAdminConsultingServices = () => {
  const { get } = Http.use();
  return get<AdminConsultingService[]>('/admin/consulting-services');
};

export const createAdminConsultingService = (req: AdminConsultingServiceCreate) => {
  const { post } = Http.use();
  return post<AdminConsultingService>('/admin/consulting-services', req);
};

export const updateAdminConsultingService = (id: string, req: AdminConsultingServiceUpdate) => {
  const { patch } = Http.use();
  return patch<AdminConsultingService>(`/admin/consulting-services/${id}`, req);
};

export const deactivateAdminConsultingService = (id: string) => {
  const http = Http.use();
  return http.delete(`/admin/consulting-services/${id}`);
};
